import React from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

const { FiX, FiAlertTriangle, FiCheck, FiCopy, FiRepeat, FiCalendar, FiList } = FiIcons

const ConfirmationModal = ({
  type,
  title,
  sourceDate,
  targetDate,
  sourcePlatos,
  targetPlatos,
  duplicates,
  hasExistingPlatos,
  hasDuplicates,
  duplicatesList,
  weeklyData,
  onConfirm,
  onCancel
}) => {

  const formatDate = (date) => {
    return format(new Date(date), "d 'de' MMMM", { locale: es })
  }

  const formatDateWithDay = (date) => {
    return format(new Date(date), "EEEE, d 'de' MMMM", { locale: es })
  }

  const categoryNames = {
    primero: 'Primer plato',
    segundo: 'Segundo plato',
    tipico: 'Plato típico',
    parrilla: 'Parrilla',
    bebida_casa: 'Bebida casa',
    bebida_botella: 'Bebida botella',
    cerveza: 'Cerveza'
  }

  const renderSingleCloneModal = () => (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Origen:</span>
            <p className="text-vip-brown font-semibold">{formatDateWithDay(sourceDate)}</p>
            <p className="text-gray-600">{sourcePlatos} platos</p>
          </div>
          <div>
            <span className="font-medium text-gray-700">Destino:</span>
            <p className="text-vip-brown font-semibold">{formatDateWithDay(targetDate)}</p>
            <p className="text-gray-600">{targetPlatos} platos existentes</p>
          </div>
        </div>
      </div>

      {/* Warning Section */}
      {hasDuplicates && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <SafeIcon icon={FiAlertTriangle} className="text-red-600 mr-3 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-900 mb-2">
                ¡Atención! Se detectaron {duplicates} platos duplicados
              </h4>
              <p className="text-red-800 text-sm mb-3">
                Los siguientes platos ya existen en la fecha de destino:
              </p>
              <div className="space-y-1">
                {duplicatesList && duplicatesList.map((plato, index) => (
                  <div key={index} className="bg-red-100 rounded px-2 py-1 text-sm">
                    <span className="font-medium">{plato.nombre}</span>
                    <span className="text-red-700 ml-2">({categoryNames[plato.categoria]})</span>
                  </div>
                ))}
              </div>
              <p className="text-red-800 text-sm mt-2 font-medium">
                Si continúas, se crearán platos duplicados en la base de datos.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      {hasExistingPlatos && !hasDuplicates && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <SafeIcon icon={FiAlertTriangle} className="text-yellow-600 mr-3 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-900 mb-1">
                La fecha de destino ya tiene platos
              </h4>
              <p className="text-yellow-800 text-sm">
                Se añadirán {sourcePlatos} platos nuevos a los {targetPlatos} platos existentes.
                No se detectaron duplicados exactos.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Section */}
      {!hasExistingPlatos && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <SafeIcon icon={FiCheck} className="text-green-600 mr-3 mt-0.5" />
            <div>
              <h4 className="font-semibold text-green-900 mb-1">
                Clonación segura
              </h4>
              <p className="text-green-800 text-sm">
                La fecha de destino está vacía. Se copiarán {sourcePlatos} platos sin conflictos.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Resumen de la operación:</h4>
        <ul className="text-blue-800 text-sm space-y-1">
          <li>• Se copiarán {sourcePlatos} platos</li>
          <li>• Fecha origen: {formatDate(sourceDate)}</li>
          <li>• Fecha destino: {formatDate(targetDate)}</li>
          {hasDuplicates && <li className="text-red-700 font-medium">• ⚠️ {duplicates} duplicados detectados</li>}
        </ul>
      </div>
    </div>
  )

  const renderWeeklyCloneModal = () => {
    if (!weeklyData) return null

    const { duplicatesByDay, totalDuplicates, totalSourcePlatos, hasExistingPlatos, hasDuplicates } = weeklyData

    return (
      <div className="space-y-6">
        {/* Header Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-center">
            <h3 className="font-semibold text-gray-900 mb-2">Clonación Semanal Completa</h3>
            <p className="text-gray-700">De la semana pasada a esta semana (Lunes a Domingo)</p>
            <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Total a clonar:</span>
                <p className="text-vip-brown font-semibold">{totalSourcePlatos} platos</p>
              </div>
              <div>
                <span className="font-medium">Duplicados:</span>
                <p className={`font-semibold ${hasDuplicates ? 'text-red-600' : 'text-green-600'}`}>
                  {totalDuplicates} platos
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Breakdown */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900">Desglose por día (Lunes a Domingo):</h4>
          {Object.entries(duplicatesByDay).map(([dayName, dayData]) => (
            <div key={dayName} className={`border rounded-lg p-3 ${
              dayData.duplicates > 0 ? 'bg-red-50 border-red-200' : 
              dayData.targetPlatos > 0 ? 'bg-yellow-50 border-yellow-200' : 
              'bg-green-50 border-green-200'
            }`}>
              <div className="flex justify-between items-start">
                <div>
                  <h5 className="font-medium capitalize">{dayName}</h5>
                  <p className="text-sm text-gray-600">
                    {formatDate(dayData.sourceDate)} → {formatDate(dayData.targetDate)}
                  </p>
                </div>
                <div className="text-right text-sm">
                  <p>Origen: {dayData.sourcePlatos} platos</p>
                  <p>Destino: {dayData.targetPlatos} platos</p>
                  {dayData.duplicates > 0 && (
                    <p className="text-red-700 font-medium">⚠️ {dayData.duplicates} duplicados</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Warning Section */}
        {hasDuplicates && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <SafeIcon icon={FiAlertTriangle} className="text-red-600 mr-3 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-900 mb-2">
                  ¡Atención! Se detectaron {totalDuplicates} platos duplicados en total
                </h4>
                <p className="text-red-800 text-sm">
                  Si continúas, se crearán platos duplicados en varios días de la semana.
                  Revisa el desglose arriba para ver qué días tienen conflictos.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info Section */}
        {hasExistingPlatos && !hasDuplicates && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <SafeIcon icon={FiAlertTriangle} className="text-yellow-600 mr-3 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-1">
                  Algunos días ya tienen platos
                </h4>
                <p className="text-yellow-800 text-sm">
                  Se añadirán platos nuevos a los días que ya tienen menús.
                  No se detectaron duplicados exactos.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Success Section */}
        {!hasExistingPlatos && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <SafeIcon icon={FiCheck} className="text-green-600 mr-3 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-900 mb-1">
                  Clonación semanal segura
                </h4>
                <p className="text-green-800 text-sm">
                  Esta semana está vacía. Se copiarán {totalSourcePlatos} platos sin conflictos.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <SafeIcon 
                icon={type === 'weekly' ? FiRepeat : FiCopy} 
                className="text-vip-orange mr-2 text-xl" 
              />
              <h2 className="text-xl font-bold text-vip-orange">{title}</h2>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          {type === 'weekly' ? renderWeeklyCloneModal() : renderSingleCloneModal()}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
            <button
              onClick={onCancel}
              className="px-6 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors duration-200"
            >
              Cancelar
            </button>
            <button
              onClick={onConfirm}
              className={`flex items-center px-6 py-2 text-white rounded-md transition-colors duration-200 ${
                hasDuplicates 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-vip-orange hover:bg-vip-dark'
              }`}
            >
              <SafeIcon 
                icon={type === 'weekly' ? FiRepeat : FiCopy} 
                className="mr-2" 
              />
              {hasDuplicates ? 'Clonar con Duplicados' : 'Confirmar Clonación'}
            </button>
          </div>

          {/* Warning Footer */}
          {hasDuplicates && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm text-center font-medium">
                ⚠️ Al confirmar, se crearán platos duplicados en la base de datos
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default ConfirmationModal